import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe, ChevronDown } from "lucide-react";
import { BRAND } from "@/data/site";
import { useI18n } from "@/context/I18nContext";

export default function Header() {
  const { t, lang, setLang, langs } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const links = [
    { label: t("nav_products"), href: "#products" },
    { label: t("nav_process"), href: "#process" },
    { label: t("nav_certs"), href: "#certifications" },
    { label: t("nav_global"), href: "#global" },
    { label: t("nav_contact"), href: "#inquiry" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const current = langs.find((l) => l.code === lang) || langs[0];

  const LangSwitcher = () => (
    <div className="relative">
      <button
        data-testid="lang-switcher"
        onClick={() => setLangOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full border border-brand-border px-3 py-2 text-sm text-brand-fg/80 hover:border-brand-gold hover:text-brand-gold transition-colors"
      >
        <Globe size={15} /> {current.label}
        <ChevronDown size={13} />
      </button>
      <AnimatePresence>
        {langOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute right-0 mt-2 w-40 overflow-hidden rounded-xl border border-brand-border bg-brand-card shadow-lg"
          >
            {langs.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => {
                  setLang(l.code);
                  setLangOpen(false);
                }}
                className={`block w-full px-4 py-2.5 text-start text-sm transition-colors hover:bg-brand-soft ${
                  l.code === lang ? "text-brand-gold font-semibold" : "text-brand-fg/80"
                }`}
              >
                {l.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-brand-bg/80 backdrop-blur-xl border-b border-brand-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-3">
          <img src="/logo.png" alt="Sunrise Mart" className="h-14 w-14 rounded-lg object-cover bg-black" />
          <div className="leading-none">
            <div className="font-display text-xl tracking-tight text-brand-fg">{BRAND.name}</div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-gold">{BRAND.tagline}</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="text-sm text-brand-fg/70 hover:text-brand-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangSwitcher />
          <a
            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
            data-testid="header-call-btn"
            className="grid place-items-center h-10 w-10 rounded-full border border-brand-border text-brand-fg/70 hover:border-brand-gold hover:text-brand-gold transition-colors"
            aria-label="Call"
          >
            <Phone size={16} />
          </a>
          <a
            href="#inquiry"
            data-testid="header-quote-btn"
            className="rounded-full bg-brand-gold px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors"
          >
            {t("cta_quote")}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LangSwitcher />
          <button data-testid="mobile-menu-toggle" className="text-brand-fg" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-brand-bg/95 backdrop-blur-xl border-b border-brand-border"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-brand-fg/80 hover:text-brand-gold transition-colors">
                  {l.label}
                </a>
              ))}
              <a href="#inquiry" onClick={() => setOpen(false)} className="rounded-full bg-brand-gold px-6 py-2.5 text-center text-sm font-semibold text-white">
                {t("cta_quote")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
